(function registerVkSizeFinder() {
  'use strict';

  if (!window.customElements || customElements.get('vk-size-finder')) {
    return;
  }

  class VkSizeFinder extends HTMLElement {
    connectedCallback() {
      if (this._connected) {
        return;
      }

      this._connected = true;
      this._controller = new AbortController();
      this._lastTrigger = null;
      this._currentResult = null;
      this._skipFocusRestore = false;

      this.engine = window.VkSizeEngine;
      if (!this.engine) {
        this.showGlobalStatus('De maatfinder kon niet worden geladen. Probeer de pagina opnieuw te laden.', true);
        return;
      }

      try {
        this.config = JSON.parse(this.querySelector('[data-vk-config]').textContent);
      } catch (error) {
        this.showGlobalStatus('De maatgegevens zijn niet geldig.', true);
        return;
      }

      var configuredProfiles = Array.isArray(this.config.profiles) ? this.config.profiles : [];
      var profileSource = this.engine.DEFAULT_PROFILES.slice();
      configuredProfiles.forEach(function mergeConfiguredProfile(profile) {
        var existingIndex = profileSource.findIndex(function sameProfile(defaultProfile) {
          return defaultProfile.key === profile.key;
        });
        if (existingIndex >= 0) {
          profileSource[existingIndex] = profile;
        } else {
          profileSource.push(profile);
        }
      });
      this.profiles = profileSource.filter(function validProfile(profile) {
        return Boolean(window.VkSizeEngine.normalizeProfile(profile));
      });

      if (!this.profiles.length) {
        this.showGlobalStatus('Er is nog geen geldig maatprofiel ingesteld.', true);
        return;
      }

      this.form = this.querySelector('[data-vk-form]');
      this.profileSelect = this.querySelector('[data-vk-profile]');
      this.chartProfileSelect = this.querySelector('[data-vk-chart-profile]');
      this.heightInput = this.querySelector('[data-vk-height]');
      this.chestInput = this.querySelector('[data-vk-chest]');
      this.resultPanel = this.querySelector('[data-vk-result]');
      this.resultHeading = this.querySelector('[data-vk-result-heading]');
      this.resultCopy = this.querySelector('[data-vk-result-copy]');
      this.resultNote = this.querySelector('[data-vk-result-note]');
      this.selectButton = this.querySelector('[data-vk-select-size]');
      this.errorBox = this.querySelector('[data-vk-errors]');
      this.chartMount = this.querySelector('[data-vk-chart]');
      this.measurementHelp = this.querySelector('[data-vk-measurement-help]');

      this.populateProfileSelects();
      this.bindEvents();
      this.updateProfileUi();
      this.renderChart(this.getActiveProfile());
    }

    disconnectedCallback() {
      if (this._controller) {
        this._controller.abort();
      }
      this._connected = false;
    }

    bindEvents() {
      var signal = this._controller.signal;
      var self = this;

      this.querySelectorAll('[data-vk-open]').forEach(function bindOpen(button) {
        button.addEventListener('click', function openRequestedDialog() {
          self.openDialog(button.getAttribute('data-vk-open'), button);
        }, { signal: signal });
      });

      this.querySelectorAll('[data-vk-close]').forEach(function bindClose(button) {
        button.addEventListener('click', function closeParentDialog() {
          self.closeDialog(button.closest('dialog'));
        }, { signal: signal });
      });

      this.querySelectorAll('dialog').forEach(function bindDialog(dialog) {
        dialog.addEventListener('click', function closeOnBackdrop(event) {
          if (event.target === dialog) {
            self.closeDialog(dialog);
          }
        }, { signal: signal });

        dialog.addEventListener('close', function restoreFocus() {
          document.documentElement.classList.remove('vk-size-finder-open');
          if (!self._skipFocusRestore && self._lastTrigger && self._lastTrigger.isConnected) {
            self._lastTrigger.focus();
          }
          self._skipFocusRestore = false;
        }, { signal: signal });

        dialog.addEventListener('cancel', function closeOnEscape(event) {
          event.preventDefault();
          self.closeDialog(dialog);
        }, { signal: signal });

        dialog.addEventListener('keydown', function closeOnEscapeFallback(event) {
          if (event.key === 'Escape') {
            event.preventDefault();
            self.closeDialog(dialog);
          }
        }, { signal: signal });
      });

      if (this.form) {
        this.form.addEventListener('submit', function calculate(event) {
          event.preventDefault();
          self.calculate();
        }, { signal: signal });
      }

      if (this.profileSelect) {
        this.profileSelect.addEventListener('change', function profileChanged() {
          self.updateProfileUi();
          self.resetResult();
        }, { signal: signal });
      }

      if (this.chartProfileSelect) {
        this.chartProfileSelect.addEventListener('change', function chartProfileChanged() {
          self.renderChart(self.getProfileByKey(self.chartProfileSelect.value));
        }, { signal: signal });
      }

      if (this.selectButton) {
        this.selectButton.addEventListener('click', function selectRecommendedSize() {
          self.applyRecommendation();
        }, { signal: signal });
      }
    }

    populateProfileSelects() {
      var activeKey = this.config.activeProfileKey;
      var activeExists = this.profiles.some(function matchesActive(profile) {
        return profile.key === activeKey;
      });

      if (!activeExists) {
        if (this.config.lockProfile && activeKey) {
          this._profileResolutionFailed = true;
          var finderTrigger = this.querySelector('[data-vk-open="finder"]');
          if (finderTrigger) {
            finderTrigger.disabled = true;
          }
          this.showGlobalStatus(
            'Het gekoppelde maatprofiel ontbreekt of is ongeldig. Controleer custom.size_profile in Shopify.',
            true
          );
        }
        activeKey = this.profiles[0].key;
      }

      [this.profileSelect, this.chartProfileSelect].forEach(function populate(select) {
        if (!select) {
          return;
        }

        select.replaceChildren();
        this.profiles.forEach(function addOption(profile) {
          var option = document.createElement('option');
          option.value = profile.key;
          option.textContent = profile.productTypeLabel || profile.label;
          select.appendChild(option);
        });
        select.value = activeKey;
      }, this);

      if (this.profileSelect && this.config.lockProfile) {
        this.profileSelect.disabled = true;
        this.profileSelect.setAttribute('aria-describedby', this.config.profileLockDescriptionId || '');
      }
    }

    getProfileByKey(key) {
      return this.profiles.find(function findProfile(profile) {
        return profile.key === key;
      }) || this.profiles[0];
    }

    getActiveProfile() {
      return this.getProfileByKey(this.profileSelect ? this.profileSelect.value : this.config.activeProfileKey);
    }

    updateProfileUi() {
      var profile = this.engine.normalizeProfile(this.getActiveProfile());
      if (!profile) {
        return;
      }

      if (this.heightInput) {
        this.heightInput.min = String(profile.inputLimits.height[0]);
        this.heightInput.max = String(profile.inputLimits.height[1]);
      }
      if (this.chestInput) {
        this.chestInput.min = String(profile.inputLimits.chest[0]);
        this.chestInput.max = String(profile.inputLimits.chest[1]);
      }
      if (this.measurementHelp) {
        this.measurementHelp.textContent = this.getActiveProfile().measurementHelp ||
          'Meet horizontaal rond het breedste gedeelte van je borst. Houd het meetlint aangesloten, maar trek het niet strak.';
      }
    }

    openDialog(name, trigger) {
      var dialog = this.querySelector('[data-vk-dialog="' + name + '"]');
      if (!dialog) {
        return;
      }

      this._lastTrigger = trigger || document.activeElement;
      document.documentElement.classList.add('vk-size-finder-open');

      if (name === 'chart') {
        if (this.chartProfileSelect && this.profileSelect) {
          this.chartProfileSelect.value = this.profileSelect.value;
        }
        this.renderChart(this.getProfileByKey(this.chartProfileSelect && this.chartProfileSelect.value));
      }

      if (typeof dialog.showModal === 'function') {
        dialog.showModal();
      } else {
        dialog.setAttribute('open', '');
        dialog.setAttribute('role', 'dialog');
        dialog.setAttribute('aria-modal', 'true');
      }

      var focusTarget = dialog.querySelector('select:not([disabled]), input:not([disabled]), button:not([disabled])');
      if (focusTarget) {
        requestAnimationFrame(function focusFirstControl() {
          focusTarget.focus();
        });
      }
    }

    closeDialog(dialog) {
      if (!dialog) {
        return;
      }

      if (typeof dialog.close === 'function' && dialog.open) {
        dialog.close();
      } else {
        dialog.removeAttribute('open');
        document.documentElement.classList.remove('vk-size-finder-open');
      }
    }

    resetResult() {
      this._currentResult = null;
      if (this.resultPanel) {
        this.resultPanel.hidden = true;
      }
      if (this.errorBox) {
        this.errorBox.hidden = true;
        this.errorBox.textContent = '';
      }
    }

    calculate() {
      this.resetResult();
      if (this._profileResolutionFailed) {
        this.showError('Het gekoppelde maatprofiel is niet geldig. Kies de maat handmatig.');
        return;
      }
      var profile = this.getActiveProfile();
      var fitInput = this.form && this.form.querySelector('[name="vk-fit-' + this.config.instanceId + '"]:checked');
      var availableSizes = this.getAvailableSizeLabels(profile);
      var result = this.engine.recommendSize({
        profile: profile,
        height: this.heightInput && this.heightInput.value,
        chest: this.chestInput && this.chestInput.value,
        fit: fitInput && fitInput.value,
        availableSizes: availableSizes
      });

      if (!result.ok) {
        this.showError(result.message, result.field);
        return;
      }

      this._currentResult = result;
      this.renderResult(result);
    }

    renderResult(result) {
      if (!this.resultPanel) {
        return;
      }

      this.resultHeading.textContent = 'Wij adviseren maat ' + result.label;
      var copy = [];

      if (result.usedFallback) {
        copy.push('Ideale maat ' + result.idealLabel + ' is niet beschikbaar bij je huidige productkeuzes. ' +
          result.label + ' is het dichtstbijzijnde beschikbare alternatief.');
      }

      copy.push('Deze maat geeft ongeveer ' + this.formatCentimeters(result.ease) +
        ' cm extra ruimte rond de borst en past het beste bij je keuze: ' + result.fitLabel.toLowerCase() + '.');

      if (result.alternate) {
        copy.push('Je zit dicht bij ' + result.label + ' en ' + result.alternate.label +
          '. Je lengte geeft hier de doorslag voor ' + result.label + '.');
      }

      if (result.stage === 'roomier') {
        copy.push('Deze beschikbare maat zal iets ruimer vallen dan je gekozen fit.');
      } else if (result.stage === 'closer') {
        copy.push('Deze maat zal aansluitender vallen dan je gekozen fit.');
      }

      this.resultCopy.textContent = copy.join(' ');

      var note = 'Borstomtrek is leidend; lengte wordt alleen bij een grensgeval gebruikt.';
      if (result.lengthAssessment === 'shorter') {
        note += ' Door je lengte kan het kledingstuk wat korter vallen.';
      } else if (result.lengthAssessment === 'longer') {
        note += ' Door je lengte kan het kledingstuk wat langer vallen.';
      }
      this.resultNote.textContent = note;

      this.selectButton.textContent = 'Selecteer maat ' + result.label;
      this.selectButton.disabled = !result.selectable;
      if (!result.selectable) {
        this.selectButton.textContent = 'Maat ' + result.label + ' niet beschikbaar';
      }

      this.resultPanel.hidden = false;
      this.resultPanel.scrollIntoView({ block: 'nearest', behavior: this.prefersReducedMotion() ? 'auto' : 'smooth' });
      this.resultHeading.setAttribute('tabindex', '-1');
      this.resultHeading.focus({ preventScroll: true });
    }

    showError(message, fieldName) {
      if (!this.errorBox) {
        return;
      }

      this.errorBox.textContent = message;
      this.errorBox.hidden = false;
      if (fieldName === 'height' && this.heightInput) {
        this.heightInput.focus();
      } else if (fieldName === 'chest' && this.chestInput) {
        this.chestInput.focus();
      } else {
        this.errorBox.focus();
      }
    }

    showGlobalStatus(message, isError) {
      var status = this.querySelector('[data-vk-global-status]');
      if (!status) {
        return;
      }

      status.textContent = message;
      status.hidden = false;
      status.classList.toggle('vk-size-finder__status--error', Boolean(isError));
    }

    renderChart(rawProfile) {
      if (!this.chartMount || !rawProfile) {
        return;
      }

      var profile = this.engine.normalizeProfile(rawProfile);
      if (!profile) {
        this.chartMount.textContent = 'De maattabel kon niet worden geladen.';
        return;
      }

      var wrapper = document.createElement('div');
      wrapper.className = 'vk-size-finder__table-scroll';
      var table = document.createElement('table');
      table.className = 'vk-size-finder__table';
      var caption = document.createElement('caption');
      caption.textContent = 'Afmetingen ' + profile.productTypeLabel + ' in centimeters';
      table.appendChild(caption);

      var thead = document.createElement('thead');
      var headRow = document.createElement('tr');
      headRow.appendChild(this.createCell('th', 'Maat', 'col'));
      profile.sizes.forEach(function addSizeHeader(size) {
        headRow.appendChild(this.createCell('th', size.label, 'col'));
      }, this);
      thead.appendChild(headRow);
      table.appendChild(thead);

      var tbody = document.createElement('tbody');
      this.appendMeasurementRow(tbody, 'Lengte', profile.sizes, 'length');
      this.appendMeasurementRow(tbody, 'Halve torso (plat)', profile.sizes, 'halfChest');
      if (profile.sizes.some(function hasSleeve(size) { return size.sleeve != null; })) {
        this.appendMeasurementRow(tbody, 'Mouwlengte', profile.sizes, 'sleeve');
      }
      table.appendChild(tbody);
      wrapper.appendChild(table);

      var explanation = document.createElement('p');
      explanation.className = 'vk-size-finder__table-help';
      explanation.textContent = 'Halve torso is de breedte van het kledingstuk wanneer het plat ligt. Voor de volledige kledingomtrek vermenigvuldig je dit getal met twee.';

      this.chartMount.replaceChildren(wrapper, explanation);
    }

    appendMeasurementRow(tbody, label, sizes, property) {
      var row = document.createElement('tr');
      row.appendChild(this.createCell('th', label, 'row'));
      sizes.forEach(function addMeasurement(size) {
        var value = size[property];
        row.appendChild(this.createCell('td', value == null ? '–' : this.formatCentimeters(value)));
      }, this);
      tbody.appendChild(row);
    }

    createCell(tagName, text, scope) {
      var cell = document.createElement(tagName);
      cell.textContent = text;
      if (scope) {
        cell.scope = scope;
      }
      return cell;
    }

    formatCentimeters(value) {
      return Number(value).toLocaleString('nl-NL', { maximumFractionDigits: 1 });
    }

    getOptionNames(rawProfile) {
      var names = [];
      if (this.config.sizeOptionName) {
        names.push(this.config.sizeOptionName);
      }
      if (rawProfile && Array.isArray(rawProfile.optionNames)) {
        names = names.concat(rawProfile.optionNames);
      }
      return names.concat(['Maat', 'Size']).filter(Boolean);
    }

    normalizeText(value) {
      return String(value == null ? '' : value)
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, ' ')
        .trim();
    }

    matchesOptionName(value, names) {
      var normalizedValue = this.normalizeText(value);
      return names.some(function matchName(name) {
        var normalizedName = this.normalizeText(name);
        return normalizedValue === normalizedName || normalizedValue.indexOf(normalizedName + ' ') === 0;
      }, this);
    }

    getProductForm() {
      if (this.config.productFormId) {
        var configuredForm = document.getElementById(this.config.productFormId);
        if (configuredForm) {
          return configuredForm;
        }
      }

      var productId = String(this.config.productId || '');
      var nearby = this.closest('.product, product-info, [data-product-id]');
      var forms = Array.from((nearby || document).querySelectorAll('form[action*="/cart/add"]'));
      if (!forms.length && nearby) {
        forms = Array.from(document.querySelectorAll('form[action*="/cart/add"]'));
      }

      return forms.find(function matchingProductForm(form) {
        var productInput = form.querySelector('[name="product-id"]');
        return form.querySelector('[name="add"]') && (!productId || (productInput && productInput.value === productId));
      }) || forms.find(function formWithSubmit(form) {
        return Boolean(form.querySelector('[name="add"]'));
      }) || null;
    }

    isAssociatedWithForm(control, form) {
      if (!form) {
        return true;
      }
      return control.form === form || form.contains(control) ||
        (form.id && control.getAttribute && control.getAttribute('form') === form.id);
    }

    findOptionGroup(rawProfile) {
      var names = this.getOptionNames(rawProfile);
      var form = this.getProductForm();
      var searchRoot = this.closest('.product, product-info, [data-product-id]') || document;
      var fieldsets = Array.from(searchRoot.querySelectorAll('fieldset'));
      if (searchRoot !== document) {
        fieldsets = fieldsets.concat(Array.from(document.querySelectorAll('fieldset')));
      }

      var fieldset = fieldsets.find(function matchingFieldset(candidate) {
        var legend = candidate.querySelector(':scope > legend');
        var controls = Array.from(candidate.querySelectorAll('input, button, select'));
        return legend && this.matchesOptionName(legend.textContent, names) &&
          controls.some(function associated(control) { return this.isAssociatedWithForm(control, form); }, this);
      }, this);

      if (fieldset) {
        return { type: 'group', element: fieldset, form: form };
      }

      var selects = Array.from(document.querySelectorAll('select'));
      var select = selects.find(function matchingSelect(candidate) {
        if (!this.isAssociatedWithForm(candidate, form)) {
          return false;
        }
        var label = candidate.id && document.querySelector('label[for="' + this.escapeSelector(candidate.id) + '"]');
        return this.matchesOptionName(candidate.name, names) ||
          (label && this.matchesOptionName(label.textContent, names)) ||
          this.matchesOptionName(candidate.getAttribute('data-option-name'), names);
      }, this);

      if (select) {
        return { type: 'select', element: select, form: form };
      }

      var dataGroups = Array.from(document.querySelectorAll('[data-option-name], [data-option]'));
      var dataGroup = dataGroups.find(function matchingDataGroup(candidate) {
        var optionName = candidate.getAttribute('data-option-name') || candidate.getAttribute('data-option');
        return this.matchesOptionName(optionName, names);
      }, this);

      return dataGroup ? { type: 'group', element: dataGroup, form: form } : null;
    }

    findSizeControl(rawProfile, label) {
      var normalizedProfile = this.engine.normalizeProfile(rawProfile);
      if (!normalizedProfile) {
        return null;
      }

      var row = normalizedProfile.sizes.find(function matchingRow(size) {
        return this.engine.rowMatchesSize(size, label);
      }, this);
      if (!row) {
        return null;
      }

      var group = this.findOptionGroup(rawProfile);
      if (!group) {
        return null;
      }

      if (group.type === 'select') {
        var option = Array.from(group.element.options).find(function matchingOption(candidate) {
          return this.engine.rowMatchesSize(row, candidate.value) || this.engine.rowMatchesSize(row, candidate.textContent);
        }, this);
        return option ? { type: 'select', control: group.element, option: option, group: group.element } : null;
      }

      var controls = Array.from(group.element.querySelectorAll('input, button, [role="radio"], [data-value], [data-option-value]'));
      var control = controls.find(function matchingControl(candidate) {
        var candidateValue = candidate.value || candidate.getAttribute('data-value') ||
          candidate.getAttribute('data-option-value') || candidate.getAttribute('aria-label') || candidate.textContent;
        return this.engine.rowMatchesSize(row, candidateValue) && this.isAssociatedWithForm(candidate, group.form);
      }, this);

      if (!control) {
        return null;
      }

      var type = control.matches('input[type="radio"], input[type="checkbox"]') ? 'checkable' : 'button';
      return { type: type, control: control, group: group.element };
    }

    getAvailableSizeLabels(rawProfile) {
      var profile = this.engine.normalizeProfile(rawProfile);
      if (!profile) {
        return null;
      }

      var foundAny = false;
      var available = [];
      profile.sizes.forEach(function inspectSize(size) {
        var descriptor = this.findSizeControl(rawProfile, size.label);
        if (!descriptor) {
          return;
        }
        foundAny = true;
        if (this.isDescriptorAvailable(descriptor)) {
          available.push(size.label);
        }
      }, this);

      return foundAny ? available : null;
    }

    isDescriptorAvailable(descriptor) {
      var control = descriptor.control;
      var option = descriptor.option;
      if ((control && control.disabled) || (option && option.disabled)) {
        return false;
      }
      if (control && control.getAttribute('aria-disabled') === 'true') {
        return false;
      }
      if ((control && control.getAttribute('data-available') === 'false') ||
        (option && option.getAttribute('data-available') === 'false')) {
        return false;
      }

      var label = control && control.id
        ? document.querySelector('label[for="' + this.escapeSelector(control.id) + '"]')
        : null;
      var unavailableClasses = /(?:^|\s)(disabled|sold-out|soldout|unavailable)(?:\s|$)/i;
      var unavailableText = /(?:uitverkocht|niet beschikbaar|niet leverbaar|sold out|unavailable)/i;
      return !(label && unavailableClasses.test(label.className)) &&
        !(control && unavailableClasses.test(control.className)) &&
        !(option && unavailableClasses.test(option.className)) &&
        !(option && unavailableText.test(option.textContent || ''));
    }

    isDescriptorSelected(descriptor) {
      if (!descriptor) {
        return false;
      }
      if (descriptor.type === 'select') {
        return descriptor.control.value === descriptor.option.value;
      }
      if (descriptor.type === 'checkable') {
        return descriptor.control.checked;
      }
      return descriptor.control.getAttribute('aria-pressed') === 'true' ||
        descriptor.control.getAttribute('aria-checked') === 'true' ||
        /(?:^|\s)(active|selected|is-selected)(?:\s|$)/i.test(descriptor.control.className);
    }

    async applyRecommendation() {
      if (!this._currentResult || !this._currentResult.selectable) {
        return;
      }

      var rawProfile = this.getActiveProfile();
      var label = this._currentResult.label;
      var descriptor = this.findSizeControl(rawProfile, label);

      if (!descriptor || !this.isDescriptorAvailable(descriptor)) {
        this.showError('Maat ' + label + ' kon niet worden geselecteerd. Kies de maat hieronder handmatig.');
        this.dispatchEvent(new CustomEvent('vk:size-selection-failed', {
          bubbles: true,
          detail: { size: label, reason: 'control_unavailable' }
        }));
        return;
      }

      var productFormBefore = this.getProductForm();
      var variantInputBefore = productFormBefore && productFormBefore.querySelector('[name="id"]');
      var variantIdBefore = variantInputBefore ? variantInputBefore.value : null;
      var wasAlreadySelected = this.isDescriptorSelected(descriptor);

      var dialog = this.querySelector('[data-vk-dialog="finder"]');
      this._skipFocusRestore = true;
      this.closeDialog(dialog);

      if (descriptor.type === 'select') {
        var valueSetter = Object.getOwnPropertyDescriptor(HTMLSelectElement.prototype, 'value').set;
        valueSetter.call(descriptor.control, descriptor.option.value);
        descriptor.control.dispatchEvent(new Event('input', { bubbles: true }));
        descriptor.control.dispatchEvent(new Event('change', { bubbles: true }));
      } else {
        descriptor.control.click();
      }

      var verification = await this.waitForVariantSelection(
        rawProfile,
        label,
        descriptor,
        variantIdBefore,
        wasAlreadySelected
      );
      var updatedDescriptor = verification.descriptor || descriptor;
      var verified = verification.verified;

      if (verified) {
        this.showGlobalStatus('Maat ' + label + ' is geselecteerd.', false);
        var focusControl = (updatedDescriptor || descriptor).control;
        if (focusControl && focusControl.focus) {
          focusControl.focus({ preventScroll: true });
        }
        this.dispatchEvent(new CustomEvent('vk:size-selected', {
          bubbles: true,
          detail: { size: label, profile: rawProfile.key, variantId: verification.variantId }
        }));
      } else {
        this.showGlobalStatus('Selecteer maat ' + label + ' hieronder; automatisch selecteren lukte niet.', true);
        this.dispatchEvent(new CustomEvent('vk:size-selection-failed', {
          bubbles: true,
          detail: { size: label, reason: 'selection_not_verified' }
        }));
      }
    }

    async waitForVariantSelection(rawProfile, label, originalDescriptor, variantIdBefore, wasAlreadySelected) {
      var deadline = Date.now() + 2500;

      while (Date.now() < deadline) {
        var descriptor = this.findSizeControl(rawProfile, label) || originalDescriptor;
        var controlSelected = this.isDescriptorSelected(descriptor);
        var currentForm = this.getProductForm();
        var currentVariantInput = currentForm && currentForm.querySelector('[name="id"]');
        var currentVariantId = currentVariantInput ? currentVariantInput.value : null;
        var needsVariantChange = Boolean(variantIdBefore) && !wasAlreadySelected;
        var variantSynced = !needsVariantChange || (Boolean(currentVariantId) && currentVariantId !== variantIdBefore);

        if (controlSelected && variantSynced) {
          return {
            verified: true,
            descriptor: descriptor,
            variantId: currentVariantId
          };
        }

        await new Promise(function waitForNextCheck(resolve) {
          window.setTimeout(resolve, 50);
        });
      }

      return {
        verified: false,
        descriptor: this.findSizeControl(rawProfile, label) || originalDescriptor,
        variantId: null
      };
    }

    escapeSelector(value) {
      if (window.CSS && typeof window.CSS.escape === 'function') {
        return window.CSS.escape(value);
      }
      return String(value).replace(/([ #;?%&,.+*~\\':"!^$[\]()=>|/@])/g, '\\$1');
    }

    prefersReducedMotion() {
      return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }
  }

  customElements.define('vk-size-finder', VkSizeFinder);
})();
